import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useState, createElement } from "react";
import { Trans } from "react-i18next";
import { post, put } from "../../../common/api/apiShared";
import { useValidator } from "../../../common/hooks/validation";
import { useTranslation } from "../../../localization/useTranslation";
import { Result } from "../../../common/api/result";
import { PaymentTermSaveModel } from "../paymentTerms/PaymentTerms.types";
import { IAddEditPaymentTermDialogProps } from "./AddEditPaymentTermDialog";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useAddEditPaymentTermDialogHooks(props: IAddEditPaymentTermDialogProps) {
    const { translateString } = useTranslation();
    const [saveModel, setSaveModel] = useState<PaymentTermSaveModel>({ name: props.selectedPaymentTerm?.name ?? "" });

    const snackbar = useDotLegalSnackbar();

    const validation = useValidator<PaymentTermSaveModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
        }),
        [saveModel]
    );
    validation.check(saveModel);

    const onModelChange = (updateFunc: (model: PaymentTermSaveModel) => void) => {
        let tempSaveModel = { ...saveModel! };

        updateFunc(tempSaveModel);
        setSaveModel(tempSaveModel);
    };

    const saveMutation = useDotLegalMutation(addPaymentTerm, {
        onSuccess: (response: Result<string>) => {
            if (response.success()) {
                snackbar.show(createElement(Trans, { i18nKey: "itemCreatedSuccess", values: { name: saveModel.name } }));
                props.onSaved();
            } else {
                snackbar.show(translateString("paymentTermExists"), "warning");
            }
        },
    });

    const updateMutation = useDotLegalMutation(updatePaymentTerm, {
        onSuccess: (response: Result<string>) => {
            if (response.success()) {
                snackbar.show(createElement(Trans, { i18nKey: "itemEditedSuccess", values: { name: saveModel.name } }));
                props.onSaved();
            } else {
                snackbar.show(translateString("paymentTermExists"), "warning");
            }
        },
    });

    const onSave = async () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        if (props.isAdd) {
            await saveMutation.mutateAsync();
        } else {
            await updateMutation.mutateAsync(props.selectedPaymentTerm!.id);
        }
    };

    function addPaymentTerm() {
        return post<string>("/paymentTerms", saveModel);
    }

    function updatePaymentTerm(id: string) {
        return put<string>(`/paymentTerms/${id}`, saveModel);
    }

    return {
        isSaving: saveMutation.isLoading || updateMutation.isLoading,
        validation,
        onModelChange,
        saveModel,
        onSave,
    };
}
